import { faSignalStream as faSignalStreamLight } from '@fortawesome/pro-light-svg-icons';
import {
  faArrowsRotate,
  faSignalStream,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Stack } from '@sixui/ui';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { QuerySkeleton } from '~/components/atoms/QuerySkeleton';
import { Breadcrumbs } from '~/components/molecules/Breadcrumbs';
import { Heading } from '~/components/molecules/Heading';
import { AppBreadcrumbs } from '~/components/organisms/Breadcrumbs';
import { LiveStream } from '~/components/organisms/LiveStream';
import { ITimeUnit } from '~/helpers/types';
import { useLiveStreamQuery } from '~/lib/api/modules/LiveStreams/hooks/useLiveStreamQuery';
import { useRefreshLiveStreamsMutation } from '~/lib/api/modules/LiveStreams/hooks/useRefreshLiveStreamsMutation';
import { LiveStreamListItemMenu } from '../molecules/LiveStreamListItem/LiveStreamListItemMenu';

const REFRESH_INTERVAL = 10 * ITimeUnit.SECOND;
const ALWAYS_REFRESH = true;

export type IAppLiveStreamPageProps = {
  appId: string;
  liveStreamId: string;
};

export const AppLiveStreamPage: React.FC<IAppLiveStreamPageProps> = (props) => {
  const { appId, liveStreamId } = props;
  const { t } = useTranslation();
  const liveStreamQuery = useLiveStreamQuery(
    {
      appId,
      liveStreamId,
    },
    {
      refetchInterval: (data) => {
        const shouldAutomaticallyRefresh =
          ALWAYS_REFRESH ||
          !!data?.recordings?.some(
            (recording) =>
              recording.state === 'started' ||
              recording.conversion?.status === 'SUBMITTED' ||
              recording.conversion?.status === 'PROGRESSING',
          );

        return shouldAutomaticallyRefresh ? REFRESH_INTERVAL : false;
      },
    },
  );
  const refreshLiveStreamsMutation = useRefreshLiveStreamsMutation();

  const liveStream = liveStreamQuery.data;
  const parentUrl = `/apps/${appId}/liveStreams`;

  return (
    <>
      <Helmet>
        <title>{t('AppLiveStreamPage.seo.title')}</title>
      </Helmet>

      <AppBreadcrumbs appId={appId}>
        <Breadcrumbs.Action href={`/apps/${appId}/liveStreams`}>
          <FontAwesomeIcon icon={faSignalStream} />
          {t('AppLiveStreamsPage.title')}
        </Breadcrumbs.Action>
      </AppBreadcrumbs>

      <Heading
        icon={<FontAwesomeIcon icon={faSignalStreamLight} />}
        title={
          <QuerySkeleton query={liveStreamQuery} length={{ min: 8, max: 16 }}>
            {t('AppLiveStreamPage.title', {
              name: liveStream?.displayName,
            })}
          </QuerySkeleton>
        }
        actions={
          <Stack horizontal gap={2}>
            {!ALWAYS_REFRESH && (
              <Button
                onClick={() =>
                  refreshLiveStreamsMutation.mutateAsync({
                    appId,
                    liveStreamId,
                  })
                }
                loading={
                  liveStreamQuery.isLoading ||
                  refreshLiveStreamsMutation.isLoading
                }
                loadingAnimation="halfSpin"
                disabled={
                  liveStreamQuery.isLoading ||
                  refreshLiveStreamsMutation.isLoading
                }
                icon={<FontAwesomeIcon icon={faArrowsRotate} />}
                variant="outlined"
              >
                {t('AppLiveStreamsPage.refresh')}
              </Button>
            )}
            {liveStream && <LiveStreamListItemMenu liveStream={liveStream} />}
          </Stack>
        }
        subtitle={t('AppLiveStreamPage.subtitle')}
        previousUrl={parentUrl}
      />

      <LiveStream appId={appId} liveStreamId={liveStreamId} />
    </>
  );
};
